<template>
  <div
    :id="fieldId"
    :class="'date-recurrence-input ' + fieldClass"
    :data-test="dataTest"
  >
    <template v-for="childElement in getChildFields">
      <entity-form-field
        v-if="showChildField(childElement)"
        :key="childElement?.field_name"
        :field-data="childElement"
        :parent-value="fieldValue?.[childElement.field_name]"
        @update-form-data="(value) => pickUpdateMethod(value)"
        :bundle="bundle"
        :entity-type="entityType"
        :show-label="false"
      />
    </template>
  </div>
</template>

<script>
export default {
  name: "DateRecurrenceField",
  components: {
    EntityFormField: () => import("../EntityFormField.vue"),
  },
  data() {
    return {
      selected: {
        recurrence: this.fieldData?.field_value?.recurrence ?? null,
        period: this.fieldData?.field_value?.period ?? "",
        frequency_qty: this.fieldData?.field_value?.frequency_qty ?? null,
        frequency_unit: this.fieldData?.field_value?.frequency_unit ?? null,
      },
    };
  },
  props: {
    fieldId: {
      type: String,
      required: true,
    },
    fieldName: {
      type: String,
      required: true,
    },
    fieldClass: {
      type: String,
      required: true,
    },
    fieldData: {
      type: Object,
      required: true,
    },
    fieldValue: {
      type: Object,
      default: function () {
        return {
          recurrence: null,
          period: null,
          frequency_qty: null,
          frequency_unit: null,
        };
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    alwaysFrequency: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataTest: {
      type: String,
      default: "",
    },
    bundle: {
      type: String,
      required: true,
    },
    entityType: {
      type: String,
      required: true,
    },
  },
  computed: {
    getChildFields() {
      let children = [];
      const childFieldSettings = this.fieldData?.settings?.child_fields;
      if (childFieldSettings) {
        Object.entries(childFieldSettings).forEach(([key, value]) => {
          children.push({
            ...value,
          });
        });
      }
      if (this.alwaysFrequency) {
        children = children.filter(
          (field) =>
            field.field_name === "frequency_qty" ||
            field.field_name === "frequency_unit",
        );
      }
      return children;
    },
  },
  mounted() {
    this.resetData();
  },
  methods: {
    showChildField(childField) {
      if (childField.field_name === "recurrence" && !this.alwaysFrequency) {
        return true;
      }
      if (
        childField.field_name === "period" &&
        this.selected.recurrence === "periodic"
      ) {
        return true;
      }
      return (
        (childField.field_name === "frequency_qty" ||
          childField.field_name === "frequency_unit") &&
        this.selected.recurrence === "frequency"
      );
    },
    updateRecurrence(value) {
      const new_period = value === "periodic" ? this.selected.period : "";
      const new_frequency_qty =
        value === "frequency" ? this.selected.frequency_qty : null;
      const new_frequency_unit =
        value === "frequency" ? this.selected.frequency_unit : "";
      this.selected.recurrence = value;
      this.selected.period = new_period;
      this.selected.frequency_qty = new_frequency_qty;
      this.selected.frequency_unit = new_frequency_unit;
    },
    updatePeriod(value) {
      this.selected.period = value;
    },
    updateFrequencyQty(value) {
      this.selected.frequency_qty = value;
    },
    updateFrequencyUnit(value) {
      this.selected.frequency_unit = value;
    },
    resetData() {
      this.selected = {
        recurrence: "",
        period: "",
        frequency_qty: null,
        frequency_unit: "",
      };
      if (!this.fieldValue) {
        return;
      }
      this.selected.recurrence = this.fieldValue?.recurrence;
      if (this.fieldValue?.recurrence === "periodic") {
        this.selected.period = this.fieldValue?.period || "";
      }
      if (this.fieldValue?.recurrence === "frequency") {
        this.selected.frequency_qty = this.fieldValue?.frequency_qty;
        this.selected.frequency_unit = this.fieldValue?.frequency_unit || "";
      }
      // Currently messaging schedule always uses frequency and hides the recurrence subfield.
      if (this.alwaysFrequency) {
        this.selected.recurrence = "frequency";
        this.selected.period = "";
      }
    },
    pickUpdateMethod(value) {
      switch (value.field_name) {
        case "recurrence":
          this.updateRecurrence(value.field_value);
          break;
        case "period":
          this.updatePeriod(value.field_value);
          break;
        case "frequency_qty":
          this.updateFrequencyQty(value.field_value);
          break;
        case "frequency_unit":
          this.updateFrequencyUnit(value.field_value);
          break;
        default:
      }
      this.$emit("change", this.selected);
    },
  },
  watch: {
    fieldValue() {
      this.resetData();
    },
  },
};
</script>

<style scoped>
.date-recurrence-input {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}
.date-recurrence-input .form-item {
  margin-top: 0;
  margin-bottom: 0;
}
</style>
